'use client';

import { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Video, FileText, Image, ChevronRight, Menu } from 'lucide-react';
import type { CollegeCourseItem, CollegeChapterItem, CollegeStepItem } from '@zrt-ai-office/types/course';
import { fetchCourseDetail, fetchCourseChapters, fetchChapterSteps } from '@/lib/academy/academy-api';
import { getCourseProgress } from '@/lib/academy/learning-progress';
import { BootcampSidebar, InstructorCard, ResourcesCard } from '@/src/components/academy';
import { ApiError } from '@/lib/api/client';
import { useAuthContext } from '@/lib/auth-context';
import { useToast } from '@/lib/hooks/use-toast';

/* ── Skeleton loader ── */
function CourseSkeleton() {
	return (
		<div className="flex min-h-screen bg-slate-50">
			<aside className="fixed top-14 left-0 bottom-0 w-72 bg-white/90 border-r border-slate-200/50" style={{ zIndex: 30 }}>
				<div className="p-5 space-y-4">
					<div className="h-5 w-40 bg-slate-200 rounded animate-pulse" />
					<div className="space-y-2">
						<div className="flex justify-between">
							<div className="h-3 w-12 bg-slate-200 rounded animate-pulse" />
							<div className="h-3 w-8 bg-slate-200 rounded animate-pulse" />
						</div>
						<div className="h-1.5 w-full bg-slate-100 rounded-full overflow-hidden">
							<div className="h-full w-0 bg-slate-200 rounded-full" />
						</div>
					</div>
					<div className="h-10 w-full bg-primary/10 rounded-xl animate-pulse" />
				</div>
			</aside>
			<main className="flex-1 lg:ml-72 pt-14">
				<div className="px-6 py-10 md:px-10">
					<div className="grid grid-cols-1 lg:grid-cols-[1fr_280px] xl:grid-cols-[1fr_320px] gap-6 xl:gap-10">
						<div>
							<div className="h-8 w-80 bg-slate-200 rounded animate-pulse mb-3" />
							<div className="h-4 w-full bg-slate-100 rounded animate-pulse mb-8" />
							<div className="space-y-3">
								{[1, 2, 3].map((i) => (
									<div className="flex items-center gap-4 h-20 px-5 bg-white rounded-xl border border-slate-200/60 animate-pulse" key={i}>
										<div className="h-10 w-10 bg-slate-200 rounded-lg shrink-0" />
										<div className="flex-1 space-y-2">
											<div className="h-4 w-2/3 bg-slate-200 rounded" />
											<div className="h-3 w-1/2 bg-slate-100 rounded" />
										</div>
									</div>
								))}
							</div>
						</div>
						<div>
							<div className="space-y-6">
								<div className="h-48 bg-white rounded-xl border border-slate-200/60 animate-pulse" />
								<div className="h-32 bg-white rounded-xl border border-slate-200/60 animate-pulse" />
							</div>
						</div>
					</div>
				</div>
			</main>
		</div>
	);
}

const STEP_ICONS: Record<string, typeof FileText> = {
	video: Video,
	md: FileText,
	image: Image,
};

export function CourseDetailPage() {
	const { courseId } = useParams<{ courseId: string }>();
	const navigate = useNavigate();
	const toast = useToast();
	const { isLoggedIn, openLogin } = useAuthContext();

	const [course, setCourse] = useState<CollegeCourseItem | null>(null);
	const [chapters, setChapters] = useState<CollegeChapterItem[]>([]);
	const [steps, setSteps] = useState<CollegeStepItem[]>([]);
	const [loading, setLoading] = useState(true);
	const [activeChapterId, setActiveChapterId] = useState<number | null>(null);
	const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);

	/* ── Fetch course + chapters on mount ── */
	useEffect(() => {
		if (!courseId) return;
		let cancelled = false;
		setLoading(true);

		Promise.all([fetchCourseDetail(courseId), fetchCourseChapters(courseId)])
			.then(([courseData, chaptersData]) => {
				if (cancelled) return;
				setCourse(courseData);
				const sorted = [...chaptersData.list].sort((a: CollegeChapterItem, b: CollegeChapterItem) => a.sortOrder - b.sortOrder);
				setChapters(sorted);
				// Auto-select: prefer saved progress, else first unlearned
				const saved = getCourseProgress(courseId!);
				let initial: CollegeChapterItem | undefined;
				if (saved) {
					initial = sorted.find((ch) => ch.id === saved.chapterId);
				}
				if (!initial) {
					initial = sorted.find((ch) => !ch.learned) ?? sorted[0];
				}
				if (initial) setActiveChapterId(initial.id);
			})
			.catch(() => {
				if (!cancelled) {
					setCourse(null);
					setChapters([]);
				}
			})
			.finally(() => {
				if (!cancelled) setLoading(false);
			});

		return () => {
			cancelled = true;
		};
	}, [courseId]);

	/* ── Fetch steps when activeChapterId changes ── */
	useEffect(() => {
		if (!activeChapterId || !courseId) {
			setSteps([]);
			return;
		}
		let cancelled = false;

		async function loadSteps() {
			try {
				const data = await fetchChapterSteps(activeChapterId!, courseId!);
				if (!cancelled) setSteps(data.list);
			} catch (err) {
				if (!cancelled) {
					if (err instanceof ApiError && err.status === 401) {
						toast.info('请先登录才能查看章节内容');
						openLogin();
					}
					setSteps([]);
				}
			}
		}

		loadSteps();
		return () => {
			cancelled = true;
		};
	}, [activeChapterId, courseId, isLoggedIn]);

	/* ── Derived data ── */
	const allCompleted = chapters.length > 0 && chapters.every((ch) => ch.learned);
	const activeChapter = useMemo(() => chapters.find((ch) => ch.id === activeChapterId) ?? null, [chapters, activeChapterId]);

	const handleSelectChapter = useCallback((chapterId: number) => {
		setActiveChapterId(chapterId);
	}, []);

	/** Continue learning: navigate to saved chapter+step or first unlearned chapter */
	const handleContinueLearning = useCallback(() => {
		if (!courseId) return;
		const saved = getCourseProgress(courseId);
		if (saved) {
			navigate(`/academy/${courseId}/${saved.chapterId}#step-${saved.stepId}`);
			return;
		}
		const first = chapters.find((ch) => !ch.learned);
		if (first) {
			navigate(`/academy/${courseId}/${first.id}`);
		}
	}, [courseId, chapters, navigate]);

	/** Navigate to lesson at specific step */
	const handleGoToStep = useCallback(
		(stepId: number) => {
			if (!courseId || !activeChapterId) return;
			navigate(`/academy/${courseId}/${activeChapterId}#step-${stepId}`);
		},
		[courseId, activeChapterId, navigate],
	);

	if (loading) return <CourseSkeleton />;

	if (!course) {
		return (
			<div className="h-dvh flex items-center justify-center pt-14">
				<p className="text-slate-400">课程不存在</p>
			</div>
		);
	}

	return (
		<div className="flex min-h-screen bg-slate-50">
			<BootcampSidebar
				activeChapterId={activeChapterId}
				allCompleted={allCompleted}
				chapters={chapters}
				courseTitle={course.title}
				mobileOpen={mobileSidebarOpen}
				onBackToCourseList={() => navigate('/academy')}
				onContinueLearning={handleContinueLearning}
				onMobileClose={() => setMobileSidebarOpen(false)}
				onSelectChapter={handleSelectChapter}
			/>

			<main className="flex-1 lg:ml-72 pt-14">
				{/* Mobile sidebar toggle */}
				<div className="lg:hidden sticky top-14 z-20 border-b border-slate-200/50 bg-white/95 backdrop-blur-md">
					<button className="w-full flex items-center gap-3 px-4 py-3 text-sm font-medium text-slate-700" onClick={() => setMobileSidebarOpen(true)} type="button">
						<Menu className="h-4 w-4" />
						<span className="truncate">章节列表</span>
					</button>
				</div>

				<div className="px-4 sm:px-6 py-8 sm:py-10 md:px-10">
					<div className="grid grid-cols-1 lg:grid-cols-[1fr_280px] xl:grid-cols-[1fr_320px] gap-6 xl:gap-10">
						<div>
							<h1 className="text-2xl md:text-3xl font-bold text-slate-900 mb-3">{course.title}</h1>
							{course.intro && <p className="text-sm text-slate-500 leading-relaxed max-w-2xl mb-8">{course.intro}</p>}

							{activeChapter ? (
								<>
									<h3 className="text-lg font-bold text-slate-900 mb-1">章节内容</h3>
									<p className="text-sm text-slate-400 mb-5">共 {steps.length} 个内容</p>

									<div className="space-y-3">
										{steps.map((step) => {
											const Icon = STEP_ICONS[step.contentType] ?? FileText;
											return (
												<button
													className="w-full flex items-center gap-4 px-5 py-4 bg-white rounded-xl border border-slate-200/60 hover:border-slate-300 hover:shadow-sm transition-all text-left group"
													key={step.id}
													onClick={() => handleGoToStep(step.id)}
													type="button"
												>
													<div className="flex items-center justify-center h-10 w-10 rounded-lg bg-slate-100 shrink-0 group-hover:bg-slate-200 transition-colors">
														<Icon className="h-4 w-4 text-slate-500" />
													</div>
													<div className="flex-1 min-w-0">
														<h4 className="text-sm font-semibold text-slate-900 truncate">{step.title}</h4>
														<span className="text-xs text-slate-400">{step.contentType === 'video' ? '视频' : step.contentType === 'md' ? '图文' : '图片'}</span>
													</div>
													<ChevronRight className="h-4 w-4 text-slate-300 group-hover:text-slate-500 shrink-0 transition-colors" />
												</button>
											);
										})}
										{steps.length === 0 && <div className="py-12 text-center text-slate-400 text-sm">暂无步骤内容</div>}
									</div>
								</>
							) : (
								<div className="py-20 text-center text-slate-400 text-sm">请在左侧选择一个章节</div>
							)}
						</div>

						<div className="self-start">
							<div className="space-y-6 sticky top-20">
								<InstructorCard />
								<ResourcesCard />
							</div>
						</div>
					</div>
					<div className="h-16" />
				</div>
			</main>
		</div>
	);
}
